import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type Socket } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { 
  MessageCircle, 
  Plus, 
  Search, 
  Send, 
  Edit3, 
  Trash2,
  Users,
  User,
  Paperclip,
  Info,
  Pin
} from 'lucide-react';
import { api } from '../lib/api';
import { createAppSocket } from '../lib/socket';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/Button';

import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { Modal } from '../components/ui/Modal';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ChatRoom {
  id: string;
  name: string;
  description?: string;
  type: 'DIRECT' | 'GROUP' | 'SUPPORT';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  participants: Array<{
    id: string;
    userId: string;
    role: 'ADMIN' | 'MEMBER' | 'READONLY';
    joinedAt: string;
    lastReadAt?: string;
    user: {
      id: string;
      name: string;
      email: string;
      role: string;
    };
  }>;
  _count: {
    messages: number;
  };
}

interface ChatMessage {
  id: string;
  roomId: string;
  senderId: string;
  content: string;
  type: 'TEXT' | 'IMAGE' | 'FILE' | 'SYSTEM';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  replyToId?: string;
  isEdited: boolean;
  editedAt?: string;
  isDeleted: boolean;
  deletedAt?: string;
  isPinned?: boolean;
  pinnedAt?: string;
  pinnedBy?: string;
  createdAt: string;
  updatedAt: string;
  sender: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  replyTo?: {
    id: string;
    content: string;
    sender: {
      id: string;
      name: string;
    };
  };
  reads: Array<{
    id: string;
    userId: string;
    readAt: string;
  }>;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

export const InternalChatPage: React.FC = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateRoomModalOpen, setIsCreateRoomModalOpen] = useState(false);
  const [isRoomInfoModalOpen, setIsRoomInfoModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [socket, setSocket] = useState<Socket | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Buscar salas de chat
  const { data: rooms = [] } = useQuery({
    queryKey: ['chat-rooms'],
    queryFn: async () => {
      const response = await api.get('/internal-chat/rooms');
      return response.data.data as ChatRoom[];
    },
  });

  // Buscar usuários disponíveis
  const { data: availableUsers = [] } = useQuery({
    queryKey: ['chat-users'],
    queryFn: async () => {
      const response = await api.get('/internal-chat/users');
      return response.data.data as User[];
    },
  });

  // Buscar mensagens da sala selecionada
  const { data: messagesData } = useQuery({
    queryKey: ['chat-messages', selectedRoom?.id],
    queryFn: async () => {
      if (!selectedRoom) return { messages: [], total: 0 };
      const response = await api.get(`/internal-chat/rooms/${selectedRoom.id}/messages`);
      return response.data.data as { messages: ChatMessage[]; total: number };
    },
    enabled: !!selectedRoom,
  });

  // Buscar mensagens (busca)
  const { data: searchResults } = useQuery({
    queryKey: ['chat-messages-search', selectedRoom?.id, searchQuery],
    queryFn: async () => {
      if (!selectedRoom || !searchQuery) return { messages: [], total: 0 };
      const response = await api.get(
        `/internal-chat/rooms/${selectedRoom.id}/messages/search?q=${encodeURIComponent(searchQuery)}`
      );
      return response.data.data as { messages: ChatMessage[]; total: number };
    },
    enabled: !!selectedRoom && !!searchQuery && isSearching,
  });

  // Mutations
  const sendMessageMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/internal-chat/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data as ChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
      queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
    },
  });

  const createRoomMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; type: string; participants: string[] }) => {
      const response = await api.post('/internal-chat/rooms', data);
      return response.data.data as ChatRoom;
    },
    onSuccess: (newRoom) => {
      queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
      setSelectedRoom(newRoom);
      setIsCreateRoomModalOpen(false);
      toast.success('Sala criada com sucesso!');
    },
  });

  const editMessageMutation = useMutation({
    mutationFn: async ({ messageId, content }: { messageId: string; content: string }) => {
      const response = await api.patch(`/internal-chat/messages/${messageId}`, { content });
      return response.data.data as ChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
      setEditingMessage(null);
      setEditContent('');
      toast.success('Mensagem editada!');
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await api.delete(`/internal-chat/messages/${messageId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
      toast.success('Mensagem deletada!');
    },
  });

  const pinMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await api.post(`/internal-chat/messages/${messageId}/pin`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
      toast.success('Mensagem fixada!');
    },
  });

  const unpinMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await api.delete(`/internal-chat/messages/${messageId}/pin`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages', selectedRoom?.id] });
      toast.success('Mensagem desfixada!');
    },
  });

  // WebSocket setup
  useEffect(() => {
    if (!user) return;

    const newSocket = createAppSocket();

    newSocket.on('connect', () => {
      console.log('Conectado ao WebSocket do chat');
      newSocket.emit('authenticate', { token: user.id });
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Desconectado do WebSocket do chat:', reason);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Erro na conexão WebSocket do chat:', error);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket do chat reconectado após', attemptNumber, 'tentativas');
      newSocket.emit('authenticate', { token: user.id });
    });

    newSocket.on('chat-message', (newMessage: ChatMessage) => {
      queryClient.setQueryData(['chat-messages', newMessage.roomId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          messages: [...old.messages, newMessage],
        };
      });
      
      // Scroll para baixo se a mensagem for da sala atual
      if (selectedRoom?.id === newMessage.roomId) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    });

    newSocket.on('chat-notification', (notification) => {
      if (selectedRoom?.id !== notification.roomId) {
        toast(`${notification.senderName}: ${notification.messagePreview}`, {
          icon: '💬',
          duration: 4000,
        });
        
        // Tocar som se habilitado
        if (soundEnabled) {
          playNotificationSound();
        }
      }
    });

    newSocket.on('user-typing', (data: { roomId: string; userId: string; isTyping: boolean }) => {
      if (selectedRoom?.id === data.roomId) {
        setTypingUsers((prev) => {
          const newSet = new Set(prev);
          if (data.isTyping) {
            newSet.add(data.userId);
          } else {
            newSet.delete(data.userId);
          }
          return newSet;
        });
      }
    });

    newSocket.on('user-status', (data: { userId: string; status: 'online' | 'offline' }) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        if (data.status === 'online') {
          newSet.add(data.userId);
        } else {
          newSet.delete(data.userId);
        }
        return newSet;
      });
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user, queryClient, selectedRoom]);

  // Entrar na sala quando selecionada
  useEffect(() => {
    if (socket && selectedRoom) {
      socket.emit('join-chat-room', { roomId: selectedRoom.id });
    }
  }, [socket, selectedRoom]);

  // Scroll para baixo quando novas mensagens chegam
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesData?.messages]);

  // Função para tocar som de notificação
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignorar erros de reprodução (navegador pode bloquear)
      });
    } catch (error) {
      // Fallback: usar beep do sistema
      console.log('\u0007'); // Beep ASCII
    }
  };


  // Função para enviar mensagem com arquivo
  const handleSendMessage = async () => {
    if ((!message.trim() && !selectedFile) || !selectedRoom) return;

    const formData = new FormData();
    formData.append('roomId', selectedRoom.id);
    formData.append('content', message.trim() || '');
    formData.append('type', 'TEXT');
    
    if (selectedFile) {
      formData.append('file', selectedFile);
    }

    sendMessageMutation.mutate(formData, {
      onSuccess: () => {
        setMessage('');
        setSelectedFile(null);
        setFilePreview(null);
        
        // Parar indicador de digitação
        if (socket) {
          socket.emit('typing-stop', { roomId: selectedRoom.id });
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Erro ao enviar mensagem');
      },
    });
  };

  // Função para detectar digitação
  const handleTyping = () => {
    if (!socket || !selectedRoom) return;

    // Emitir typing-start
    socket.emit('typing-start', { roomId: selectedRoom.id });

    // Limpar timeout anterior
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Parar após 3 segundos sem digitar
    typingTimeoutRef.current = setTimeout(() => {
      if (socket && selectedRoom) {
        socket.emit('typing-stop', { roomId: selectedRoom.id });
      }
    }, 3000);
  };

  const handleCreateRoom = (formData: { name: string; description?: string; type: string; participants: string[] }) => {
    createRoomMutation.mutate(formData);
  };

  const handleEditMessage = () => {
    if (!editingMessage || !editContent.trim()) return;

    editMessageMutation.mutate({
      messageId: editingMessage,
      content: editContent.trim(),
    });
  };

  const handleDeleteMessage = (messageId: string) => {
    if (confirm('Tem certeza que deseja deletar esta mensagem?')) {
      deleteMessageMutation.mutate(messageId);
    }
  };

  const filteredRooms = rooms.filter(room =>
    room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    room.participants.some(p => p.user.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getRoomDisplayName = (room: ChatRoom) => {
    if (room.type === 'DIRECT') {
      const otherParticipant = room.participants.find(p => p.user.id !== user?.id);
      return otherParticipant?.user.name || room.name;
    }
    return room.name;
  };

  const getRoomAvatar = (room: ChatRoom) => {
    if (room.type === 'DIRECT') {
      const otherParticipant = room.participants.find(p => p.user.id !== user?.id);
      return otherParticipant?.user.name.charAt(0).toUpperCase() || '?';
    }
    return room.name.charAt(0).toUpperCase();
  };

  return (
    <div className="flex h-screen bg-muted/50">
      {/* Sidebar - Lista de salas */}
      <div className="w-80 bg-white border-r border-border flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-foreground">Chat Interno</h1>
            <Button
              onClick={() => setIsCreateRoomModalOpen(true)}
              size="sm"
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nova Sala
            </Button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Buscar salas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Lista de salas */}
        <div className="flex-1 overflow-y-auto">
          {filteredRooms.map((room) => (
            <div
              key={room.id}
              onClick={() => setSelectedRoom(room)}
              className={`p-4 border-b border-border bg-gray-50 dark:bg-slate-700 border-gray-100 dark:border-slate-700 dark:border-slate-700 cursor-pointer hover:bg-muted/50 transition-colors ${
                selectedRoom?.id === room.id ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <span className="text-sm font-medium">
                    {getRoomAvatar(room)}
                  </span>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-foreground truncate">
                      {getRoomDisplayName(room)}
                    </h3>
                    <div className="flex items-center gap-1">
                      {room.type === 'DIRECT' && <User className="h-3 w-3 text-gray-400" />}
                      {room.type === 'GROUP' && <Users className="h-3 w-3 text-gray-400" />}
                      {room.type === 'SUPPORT' && <MessageCircle className="h-3 w-3 text-gray-400" />}
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-1">
                    {room.participants.length} participante{room.participants.length !== 1 ? 's' : ''}
                    {room._count.messages > 0 && (
                      <span className="ml-2">• {room._count.messages} mensagens</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {filteredRooms.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhuma sala encontrada</p>
            </div>
          )}
        </div>
      </div>

      {/* Área principal do chat */}
      <div className="flex-1 flex flex-col">
        {selectedRoom ? (
          <>
            {/* Header da sala */}
            <div className="bg-white border-b border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <span className="text-sm font-medium">
                      {getRoomAvatar(selectedRoom)}
                    </span>
                  </Avatar>
                  
                  <div>
                    <h2 className="text-lg font-semibold text-foreground text-foreground">
                      {getRoomDisplayName(selectedRoom)}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {selectedRoom.participants.length} participante{selectedRoom.participants.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSearching(!isSearching)}
                    title="Buscar mensagens"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    title={soundEnabled ? 'Desativar som' : 'Ativar som'}
                  >
                    {soundEnabled ? '🔔' : '🔕'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsRoomInfoModalOpen(true)}
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Indicador de digitação */}
            {typingUsers.size > 0 && (
              <div className="px-4 py-2 text-sm text-muted-foreground italic">
                {Array.from(typingUsers).map((userId) => {
                  const user = availableUsers.find((u) => u.id === userId);
                  return user?.name;
                }).filter(Boolean).join(', ')} está digitando...
              </div>
            )}

            {/* Busca de mensagens */}
            {isSearching && (
              <div className="px-4 py-2 border-b border-border">
                <div className="flex items-center gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar mensagens..."
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsSearching(false);
                      setSearchQuery('');
                    }}
                  >
                    ×
                  </Button>
                </div>
              </div>
            )}

            {/* Mensagens */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
              onScroll={(e) => {
                const target = e.target as HTMLDivElement;
                const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
                setIsAtBottom(isBottom);
              }}
            >
              {/* Mensagens fixadas */}
              {((isSearching ? searchResults : messagesData)?.messages?.filter((msg: any) => msg.isPinned)?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                    📌 Mensagens Fixadas
                  </div>
                  {(isSearching ? searchResults : messagesData)?.messages
                    .filter((msg: any) => msg.isPinned)
                    .map((msg: any) => (
                      <div key={msg.id} className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                        <div className="text-xs text-muted-foreground">{msg.sender.name}</div>
                        <div className="text-sm">{msg.content}</div>
                      </div>
                    ))}
                </div>
              )}

              {(isSearching ? searchResults : messagesData)?.messages
                .filter((msg: any) => !msg.isPinned)
                .map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender.id === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-xs lg:max-w-md ${msg.sender.id === user?.id ? 'order-2' : 'order-1'}`}>
                    {msg.sender.id !== user?.id && (
                      <div className="flex items-center gap-2 mb-1">
                        <Avatar className="h-6 w-6">
                          <span className="text-xs">
                            {msg.sender.name.charAt(0).toUpperCase()}
                          </span>
                        </Avatar>
                        <span className="text-xs font-medium text-foreground">
                          {msg.sender.name}
                        </span>
                      </div>
                    )}
                    
                    <div
                      className={`p-3 rounded-lg ${
                        msg.sender.id === user?.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      {msg.replyTo && (
                        <div className={`text-xs mb-2 p-2 rounded ${
                          msg.sender.id === user?.id
                            ? 'bg-blue-400 text-white'
                            : 'bg-gray-200 text-muted-foreground'
                        }`}>
                          <div className="font-medium">Respondendo a {msg.replyTo.sender.name}</div>
                          <div className="truncate">{msg.replyTo.content}</div>
                        </div>
                      )}
                      
                      <div className="whitespace-pre-wrap">
                        {msg.content.split(/(@\w+)/g).map((part: string, idx: number) => {
                          if (part.startsWith('@')) {
                            return (
                              <span key={idx} className="font-semibold text-blue-600 dark:text-blue-400">
                                {part}
                              </span>
                            );
                          }
                          // Detectar links
                          const urlRegex = /(https?:\/\/[^\s]+)/g;
                          return part.split(urlRegex).map((urlPart: string, urlIdx: number) => {
                            if (urlRegex.test(urlPart)) {
                              return (
                                <a
                                  key={`${idx}-${urlIdx}`}
                                  href={urlPart}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 underline"
                                >
                                  {urlPart}
                                </a>
                              );
                            }
                            return urlPart;
                          });
                        })}
                      </div>
                      
                      {/* Exibir arquivo se houver */}
                      {msg.fileUrl && (
                        <div className="mt-2">
                          {msg.type === 'IMAGE' ? (
                            <img 
                              src={msg.fileUrl} 
                              alt={msg.fileName || 'Imagem'} 
                              className="max-w-xs rounded-lg cursor-pointer"
                              onClick={() => window.open(msg.fileUrl, '_blank')}
                            />
                          ) : (
                            <a
                              href={msg.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              <Paperclip className="w-4 h-4" />
                              <span>{msg.fileName || 'Arquivo'}</span>
                              {msg.fileSize && (
                                <span className="text-xs text-gray-500">
                                  ({(msg.fileSize / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </a>
                          )}
                        </div>
                      )}
                      
                      {msg.isEdited && (
                        <div className="text-xs opacity-70 mt-1">(editado)</div>
                      )}
                      
                      {msg.isPinned && (
                        <div className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                          <Pin className="w-3 h-3" />
                          <span>Mensagem fixada</span>
                        </div>
                      )}
                      
                      <div className="text-xs opacity-70 mt-1">
                        {formatDistanceToNow(new Date(msg.createdAt), { 
                          addSuffix: true, 
                          locale: ptBR 
                        })}
                      </div>
                    </div>
                    
                    {/* Botões de ação (apenas para mensagens próprias ou admins) */}
                    {(msg.sender.id === user?.id || selectedRoom?.participants.find(p => p.userId === user?.id && p.role === 'ADMIN')) && (
                      <div className="flex items-center gap-1 mt-1 justify-end">
                        {selectedRoom?.participants.find(p => p.userId === user?.id && p.role === 'ADMIN') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (msg.isPinned) {
                                unpinMessageMutation.mutate(msg.id);
                              } else {
                                pinMessageMutation.mutate(msg.id);
                              }
                            }}
                            className="h-6 w-6 p-0"
                            title={msg.isPinned ? 'Desfixar mensagem' : 'Fixar mensagem'}
                          >
                            <Pin className={`h-3 w-3 ${msg.isPinned ? 'text-yellow-600 fill-yellow-600' : ''}`} />
                          </Button>
                        )}
                        {msg.sender.id === user?.id && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingMessage(msg.id);
                                setEditContent(msg.content);
                              }}
                              className="h-6 w-6 p-0"
                              title="Editar mensagem"
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              title="Deletar mensagem"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Status online/offline do remetente (apenas na primeira mensagem do grupo) */}
                    {msg.sender.id !== user?.id && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className={`w-2 h-2 rounded-full ${
                          onlineUsers.has(msg.sender.id) ? 'bg-green-500' : 'bg-gray-400'
                        }`} title={onlineUsers.has(msg.sender.id) ? 'Online' : 'Offline'} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
              
              {/* Botão para voltar ao final se não estiver no bottom */}
              {!isAtBottom && (
                <div className="fixed bottom-24 right-8">
                  <Button
                    onClick={() => {
                      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                      setIsAtBottom(true);
                    }}
                    size="sm"
                    className="rounded-full shadow-lg"
                  >
                    ↓ Novas mensagens
                  </Button>
                </div>
              )}
            </div>

            {/* Input de mensagem */}
            <div className="bg-card border-t border-border p-4">
              {editingMessage ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="Editar mensagem..."
                    onKeyPress={(e) => e.key === 'Enter' && handleEditMessage()}
                    className="flex-1"
                  />
                  <Button onClick={handleEditMessage} size="sm">
                    Salvar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingMessage(null);
                      setEditContent('');
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-10 w-10 p-0"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  
                  <Input
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      handleTyping();
                    }}
                    placeholder="Digite sua mensagem... (use @ para mencionar)"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="flex-1"
                  />
                  
                  <Button
                    onClick={handleSendMessage}
                    disabled={(!message.trim() && !selectedFile) || sendMessageMutation.isPending}
                    size="sm"
                    className="h-10 w-10 p-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,audio/*,video/*,text/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedFile(file);
                    // Preview para imagens
                    if (file.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        setFilePreview(e.target?.result as string);
                      };
                      reader.readAsDataURL(file);
                    } else {
                      setFilePreview('file');
                    }
                  }
                }}
              />
              
              {/* Preview de arquivo selecionado */}
              {selectedFile && (
                <div className="mt-2 p-2 bg-muted rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {filePreview && filePreview !== 'file' ? (
                      <img src={filePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <Paperclip className="w-5 h-5 text-gray-500" />
                    )}
                    <span className="text-sm text-foreground truncate max-w-xs">
                      {selectedFile.name}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      setFilePreview(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="h-6 w-6 p-0"
                  >
                    ×
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Selecione uma sala
              </h3>
              <p className="text-muted-foreground">
                Escolha uma sala de chat para começar a conversar
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de criar sala */}
      <CreateRoomModal
        isOpen={isCreateRoomModalOpen}
        onClose={() => setIsCreateRoomModalOpen(false)}
        onCreateRoom={handleCreateRoom}
        availableUsers={availableUsers}
        isLoading={createRoomMutation.isPending}
      />

      {/* Modal de informações da sala */}
      <RoomInfoModal
        isOpen={isRoomInfoModalOpen}
        onClose={() => setIsRoomInfoModalOpen(false)}
        room={selectedRoom}
      />
    </div>
  );
};

// Componente Modal para criar sala
interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateRoom: (data: { name: string; description?: string; type: string; participants: string[] }) => void;
  availableUsers: User[];
  isLoading: boolean;
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
  isOpen,
  onClose,
  onCreateRoom,
  availableUsers,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'DIRECT',
    participants: [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || formData.participants.length === 0) return;
    
    onCreateRoom(formData);
    setFormData({ name: '', description: '', type: 'DIRECT', participants: [] });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Criar Nova Sala">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Nome da Sala
          </label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Digite o nome da sala"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Descrição (opcional)
          </label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Digite uma descrição"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Tipo de Sala
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value="DIRECT">Chat Direto</option>
            <option value="GROUP">Grupo</option>
            <option value="SUPPORT">Suporte</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-1">
            Participantes
          </label>
          <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
            {availableUsers.map((user) => (
              <label key={user.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded">
                <input
                  type="checkbox"
                  checked={formData.participants.includes(user.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        participants: [...formData.participants, user.id],
                      });
                    } else {
                      setFormData({
                        ...formData,
                        participants: formData.participants.filter(id => id !== user.id),
                      });
                    }
                  }}
                />
                <span className="text-sm">{user.name}</span>
                <Badge variant="secondary" className="text-xs">
                  {user.role}
                </Badge>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Criando...' : 'Criar Sala'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Componente Modal para informações da sala
interface RoomInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  room: ChatRoom | null;
}

const RoomInfoModal: React.FC<RoomInfoModalProps> = ({ isOpen, onClose, room }) => {
  if (!room) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Informações da Sala">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-foreground">{room.name}</h3>
          {room.description && (
            <p className="text-muted-foreground mt-1">{room.description}</p>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-foreground mb-2">Participantes</h4>
          <div className="space-y-2">
            {room.participants.map((participant) => (
              <div key={participant.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <span className="text-sm">
                      {participant.user.name.charAt(0).toUpperCase()}
                    </span>
                  </Avatar>
                  <div>
                    <div className="text-sm font-medium">{participant.user.name}</div>
                    <div className="text-xs text-muted-foreground">{participant.user.email}</div>
                  </div>
                </div>
                <Badge variant={participant.role === 'ADMIN' ? 'default' : 'secondary'}>
                  {participant.role}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          Criada em {new Date(room.createdAt).toLocaleDateString('pt-BR')}
        </div>
      </div>
    </Modal>
  );
};
